import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfigResponse } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createScopeCondition } from '@/components/backupStatus/configurationStatusScope';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { TagsElasticContainer } from '@/components/tags/tagsElasticContainer';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { EntraIdResourceSelectionViewer } from './EntraIdResourceSelectionViewer';
import { GwsResourceSelectionViewer } from './GwsResourceSelectionViewer';
import { M365ResourceSelectionViewer } from './M365ResourceSelectionViewer';
import { backupPolicyNativeProvider, isAzureEntraIdPolicyType, isContinuousPolicyType, isGoogleWorkspacePolicyType, isMicrosoft365PolicyType, isNativePolicyType, isPitrPolicyType, isSingleTenantSaasPolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyContinuousSchedulesTable, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfigResponse;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfigResponse) => void;
  onDelete?: (policy: BackupPolicyConfigResponse) => void;
  onDuplicate?: (policy: BackupPolicyConfigResponse) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfigResponse) => Promise<boolean>;
}
export function BackupPolicyCard(props) {
  const $ = _c(177);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const {
    InventoryStatusColumns
  } = useFeatureFlags();
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let t0;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== InventoryStatusColumns || $[1] !== allBackupPolicyProperties || $[2] !== enabled || $[3] !== isAuthorized || $[4] !== props || $[5] !== snackbar || $[6] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t16;
    if ($[30] !== props.policy.condition) {
      t16 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[30] = props.policy.condition;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    const resourcesScopeType = t16;
    let t17;
    if ($[32] !== props.policy.policyType) {
      t17 = isSingleTenantSaasPolicyType(props.policy.policyType);
      $[32] = props.policy.policyType;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    const isSaasWorkspace = t17;
    let t18;
    if ($[34] !== props || $[35] !== snackbar) {
      t18 = async () => {
        const enabled_0 = props.policy.enabled;
        const updated = await props.onEnabledPolicyUpdate?.(props.policy);
        if (updated === false) {
          return;
        }
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[34] = props;
      $[35] = snackbar;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    togglePolicyEnabledState = t18;
    let t19;
    if ($[37] !== enabled || $[38] !== togglePolicyEnabledState) {
      t19 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[37] = enabled;
      $[38] = togglePolicyEnabledState;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    const onToggleClicked = t19;
    const t20 = props.policy?.id || "";
    let T6;
    let T7;
    let t21;
    let t22;
    let t23;
    let t24;
    let t25;
    let t26;
    if ($[40] !== InventoryStatusColumns || $[41] !== enabled || $[42] !== isAuthorized || $[43] !== isSaasWorkspace || $[44] !== props.onDelete || $[45] !== props.onDuplicate || $[46] !== props.onEdit || $[47] !== props.onEnabledPolicyUpdate || $[48] !== props.policy.attachedResources || $[49] !== props.policy.name || $[50] !== props.policy.policyType || $[51] !== props.processing || $[52] !== t20) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t20],
        property: backupPolicies.name
      };
      T5 = BPCard;
      t14 = !enabled;
      t15 = "backup-policy-card";
      T7 = Stack;
      t24 = "row";
      if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[64] = t25;
      } else {
        t25 = $[64];
      }
      let t27;
      if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
        t27 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[65] = t27;
      } else {
        t27 = $[65];
      }
      let t28;
      if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
        t28 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[66] = t28;
      } else {
        t28 = $[66];
      }
      let t29;
      if ($[67] !== props.policy.name) {
        t29 = <Stack sx={t28}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[67] = props.policy.name;
        $[68] = t29;
      } else {
        t29 = $[68];
      }
      let t30;
      if ($[69] !== props.policy.policyType) {
        t30 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[69] = props.policy.policyType;
        $[70] = t30;
      } else {
        t30 = $[70];
      }
      if ($[71] !== t29 || $[72] !== t30) {
        t26 = <Stack direction="row" sx={t27}>{t29}{t30}</Stack>;
        $[71] = t29;
        $[72] = t30;
        $[73] = t26;
      } else {
        t26 = $[73];
      }
      T6 = Stack;
      t21 = "row";
      if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
        t22 = {
          alignItems: "center",
          gap: "16px"
        };
        $[74] = t22;
      } else {
        t22 = $[74];
      }
      t23 = enabled && !isSaasWorkspace && <>{props.processing ? <Loader size="medium" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createScopeCondition("backupPolicyResources", [BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.AccessLost, BackupStatus.Unsupported, BackupStatus.ClassificationFailed, BackupStatus.LimitExceeded], InventoryStatusColumns)])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} ${pluralize("Resource", props.policy.attachedResources ?? 0)}`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[40] = InventoryStatusColumns;
      $[41] = enabled;
      $[42] = isAuthorized;
      $[43] = isSaasWorkspace;
      $[44] = props.onDelete;
      $[45] = props.onDuplicate;
      $[46] = props.onEdit;
      $[47] = props.onEnabledPolicyUpdate;
      $[48] = props.policy.attachedResources;
      $[49] = props.policy.name;
      $[50] = props.policy.policyType;
      $[51] = props.processing;
      $[52] = t20;
      $[53] = T5;
      $[54] = T6;
      $[55] = T7;
      $[56] = t14;
      $[57] = t15;
      $[58] = t21;
      $[59] = t22;
      $[60] = t23;
      $[61] = t24;
      $[62] = t25;
      $[63] = t26;
    } else {
      T5 = $[53];
      T6 = $[54];
      T7 = $[55];
      t14 = $[56];
      t15 = $[57];
      t21 = $[58];
      t22 = $[59];
      t23 = $[60];
      t24 = $[61];
      t25 = $[62];
      t26 = $[63];
    }
    let t27;
    if ($[75] !== isAuthorized || $[76] !== isSaasWorkspace || $[77] !== props) {
      t27 = (isSaasWorkspace && props.onDelete || props.onEdit && props.onDelete && props.onDuplicate) && <OptionMenu options={[...(isSaasWorkspace ? [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }] : [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }]), {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[75] = isAuthorized;
      $[76] = isSaasWorkspace;
      $[77] = props;
      $[78] = t27;
    } else {
      t27 = $[78];
    }
    let t28;
    if ($[79] !== enabled || $[80] !== isAuthorized || $[81] !== onToggleClicked || $[82] !== props.onEnabledPolicyUpdate) {
      t28 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[79] = enabled;
      $[80] = isAuthorized;
      $[81] = onToggleClicked;
      $[82] = props.onEnabledPolicyUpdate;
      $[83] = t28;
    } else {
      t28 = $[83];
    }
    let t29;
    if ($[84] !== T6 || $[85] !== t21 || $[86] !== t22 || $[87] !== t23 || $[88] !== t27 || $[89] !== t28) {
      t29 = <T6 direction={t21} sx={t22}>{t23}{t27}{t28}</T6>;
      $[84] = T6;
      $[85] = t21;
      $[86] = t22;
      $[87] = t23;
      $[88] = t27;
      $[89] = t28;
      $[90] = t29;
    } else {
      t29 = $[90];
    }
    if ($[91] !== T7 || $[92] !== t24 || $[93] !== t25 || $[94] !== t26 || $[95] !== t29) {
      t12 = <T7 direction={t24} sx={t25}>{t26}{t29}</T7>;
      $[91] = T7;
      $[92] = t24;
      $[93] = t25;
      $[94] = t26;
      $[95] = t29;
      $[96] = t12;
    } else {
      t12 = $[96];
    }
    if ($[97] !== props.policy.tags) {
      t13 = !!Object.keys(props.policy.tags ?? {}).length && <Stack className="px-[24px] pt-[12px]"><TagsElasticContainer tags={props.policy.tags ?? {}} expandable={true} /></Stack>;
      $[97] = props.policy.tags;
      $[98] = t13;
    } else {
      t13 = $[98];
    }
    T4 = CardContent;
    if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        marginTop: "24px",
        "&&": {
          padding: "0 0 24px"
        }
      };
      $[99] = t11;
    } else {
      t11 = $[99];
    }
    T3 = Stack;
    if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Divider />;
      t9 = {
        gap: "12px"
      };
      $[100] = t8;
      $[101] = t9;
    } else {
      t8 = $[100];
      t9 = $[101];
    }
    let t30;
    if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = {
        px: "24px"
      };
      $[102] = t30;
    } else {
      t30 = $[102];
    }
    let t31;
    if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[103] = t31;
    } else {
      t31 = $[103];
    }
    let t32;
    if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = {
        gap: "24px"
      };
      $[104] = t32;
    } else {
      t32 = $[104];
    }
    let t33;
    if ($[105] !== allBackupPolicyProperties || $[106] !== props.policy.condition || $[107] !== props.policy.exclude || $[108] !== props.policy.include || $[109] !== props.policy.policyType || $[110] !== props.policy.resourceTypes || $[111] !== resourcesScopeType) {
      t33 = isGoogleWorkspacePolicyType(props.policy.policyType) ? <GwsResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isMicrosoft365PolicyType(props.policy.policyType) ? <M365ResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isAzureEntraIdPolicyType(props.policy.policyType) ? <EntraIdResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : <ResourcesSelectionViewer condition={props.policy.condition} conditionTitle={<BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD} />} include={props.policy.include ?? []} exclude={props.policy.exclude ?? []} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too."} supportProperties={allBackupPolicyProperties.flat()} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[105] = allBackupPolicyProperties;
      $[106] = props.policy.condition;
      $[107] = props.policy.exclude;
      $[108] = props.policy.include;
      $[109] = props.policy.policyType;
      $[110] = props.policy.resourceTypes;
      $[111] = resourcesScopeType;
      $[112] = t33;
    } else {
      t33 = $[112];
    }
    let t34;
    if ($[113] !== props.policy.policyType || $[114] !== props.policy.vaultStorageTier) {
      t34 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} vaultStorageTier={props.policy.vaultStorageTier} />;
      $[113] = props.policy.policyType;
      $[114] = props.policy.vaultStorageTier;
      $[115] = t34;
    } else {
      t34 = $[115];
    }
    let t35;
    if ($[116] !== t33 || $[117] !== t34) {
      t35 = <AccordionDetails><Stack sx={t32}>{t33}{t34}</Stack></AccordionDetails>;
      $[116] = t33;
      $[117] = t34;
      $[118] = t35;
    } else {
      t35 = $[118];
    }
    if ($[119] !== props.defaultExpanded || $[120] !== t35) {
      t10 = <Accordion defaultExpanded={props.defaultExpanded} sx={t30}>{t31}{t35}</Accordion>;
      $[119] = props.defaultExpanded;
      $[120] = t35;
      $[121] = t10;
    } else {
      t10 = $[121];
    }
    T2 = Accordion;
    t5 = vaultsSectionExpanded;
    if ($[122] !== vaultsSectionExpanded) {
      t6 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[122] = vaultsSectionExpanded;
      $[123] = t6;
    } else {
      t6 = $[123];
    }
    if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        px: "24px"
      };
      $[124] = t7;
    } else {
      t7 = $[124];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t0 = "row";
    t1 = "w-full";
    if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      t3 = <Typography variant="h4">Backup schedules</Typography>;
      $[125] = t2;
      $[126] = t3;
    } else {
      t2 = $[125];
      t3 = $[126];
    }
    t4 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = InventoryStatusColumns;
    $[1] = allBackupPolicyProperties;
    $[2] = enabled;
    $[3] = isAuthorized;
    $[4] = props;
    $[5] = snackbar;
    $[6] = vaultsSectionExpanded;
    $[7] = T0;
    $[8] = T1;
    $[9] = T2;
    $[10] = T3;
    $[11] = T4;
    $[12] = T5;
    $[13] = t0;
    $[14] = t1;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = togglePolicyEnabledState;
  } else {
    T0 = $[7];
    T1 = $[8];
    T2 = $[9];
    T3 = $[10];
    T4 = $[11];
    T5 = $[12];
    t0 = $[13];
    t1 = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t15 = $[20];
    t2 = $[21];
    t3 = $[22];
    t4 = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
    togglePolicyEnabledState = $[29];
  }
  let t16;
  if ($[127] !== T0 || $[128] !== t0 || $[129] !== t1 || $[130] !== t2 || $[131] !== t3 || $[132] !== t4) {
    t16 = <T0 direction={t0} className={t1} sx={t2}>{t3}{t4}</T0>;
    $[127] = T0;
    $[128] = t0;
    $[129] = t1;
    $[130] = t2;
    $[131] = t3;
    $[132] = t4;
    $[133] = t16;
  } else {
    t16 = $[133];
  }
  let t17;
  if ($[134] !== T1 || $[135] !== t16) {
    t17 = <T1>{t16}</T1>;
    $[134] = T1;
    $[135] = t16;
    $[136] = t17;
  } else {
    t17 = $[136];
  }
  let t18;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[137] = t18;
  } else {
    t18 = $[137];
  }
  let t19;
  if ($[138] !== props.policy || $[139] !== props.vaults) {
    t19 = <AccordionDetails sx={t18}>{isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />}</AccordionDetails>;
    $[138] = props.policy;
    $[139] = props.vaults;
    $[140] = t19;
  } else {
    t19 = $[140];
  }
  let t20;
  if ($[141] !== T2 || $[142] !== t17 || $[143] !== t19 || $[144] !== t5 || $[145] !== t6 || $[146] !== t7) {
    t20 = <T2 expanded={t5} onChange={t6} sx={t7}>{t17}{t19}</T2>;
    $[141] = T2;
    $[142] = t17;
    $[143] = t19;
    $[144] = t5;
    $[145] = t6;
    $[146] = t7;
    $[147] = t20;
  } else {
    t20 = $[147];
  }
  let t21;
  if ($[148] !== T3 || $[149] !== t10 || $[150] !== t20 || $[151] !== t8 || $[152] !== t9) {
    t21 = <T3 divider={t8} sx={t9}>{t10}{t20}</T3>;
    $[148] = T3;
    $[149] = t10;
    $[150] = t20;
    $[151] = t8;
    $[152] = t9;
    $[153] = t21;
  } else {
    t21 = $[153];
  }
  let t22;
  if ($[154] !== T4 || $[155] !== t11 || $[156] !== t21) {
    t22 = <T4 sx={t11}>{t21}</T4>;
    $[154] = T4;
    $[155] = t11;
    $[156] = t21;
    $[157] = t22;
  } else {
    t22 = $[157];
  }
  let t23;
  if ($[158] !== props.policy.name) {
    t23 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[158] = props.policy.name;
    $[159] = t23;
  } else {
    t23 = $[159];
  }
  let t24;
  if ($[160] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setDisablePolicyDialogOpen(false);
    $[160] = t24;
  } else {
    t24 = $[160];
  }
  let t25;
  if ($[161] !== togglePolicyEnabledState) {
    t25 = async () => {
      await togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[161] = togglePolicyEnabledState;
    $[162] = t25;
  } else {
    t25 = $[162];
  }
  let t26;
  if ($[163] !== disablePolicyDialogOpen || $[164] !== t23 || $[165] !== t25) {
    t26 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t23} onCancel={t24} onConfirm={t25} buttonTitle="Disable Policy" />;
    $[163] = disablePolicyDialogOpen;
    $[164] = t23;
    $[165] = t25;
    $[166] = t26;
  } else {
    t26 = $[166];
  }
  let t27;
  if ($[167] !== t12 || $[168] !== t13 || $[169] !== t22 || $[170] !== t26) {
    t27 = <>{t12}{t13}{t22}{t26}</>;
    $[167] = t12;
    $[168] = t13;
    $[169] = t22;
    $[170] = t26;
    $[171] = t27;
  } else {
    t27 = $[171];
  }
  let t28;
  if ($[172] !== T5 || $[173] !== t14 || $[174] !== t15 || $[175] !== t27) {
    t28 = <T5 disabled={t14} data-testid={t15}>{t27}</T5>;
    $[172] = T5;
    $[173] = t14;
    $[174] = t15;
    $[175] = t27;
    $[176] = t28;
  } else {
    t28 = $[176];
  }
  return t28;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}